import React, { useState, useEffect } from "react";
import { Modal, Tabs, Select, Input, Switch, Slider, Button, message, Spin, Alert, Form, Tag, Tooltip, Descriptions, List, Card, Typography } from "antd";
import { GlobalOutlined, SettingOutlined, ThunderboltOutlined, ExperimentOutlined, SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import axios from "axios";
import appConfig from "../../utils/config"; // Assumes appConfig has apiURL

const { TabPane } = Tabs;
const { TextArea } = Input;

const SCHEMAS = {
    quickStats: {
        type: "object",
        required: ["quickStats", "overview", "curationText"],
        properties: {
            curationText: { type: "string", description: "Dynamically generated 1-sentence editor's note explaining curation." },
            quickStats: {
                type: "object",
                required: ["paths", "salaryRange", "level"],
                properties: {
                    paths: { type: "integer", description: "Number of major career paths that use this skill." },
                    salaryRange: { type: "string", description: "Representative annual US salary range." },
                    level: { type: "string", description: "Beginner, Intermediate, Advanced, or Beginner to Advanced." }
                }
            },
            overview: {
                type: "object",
                required: ["whyRelevant", "industryDemand", "whereUsed", "description"],
                properties: {
                    description: { type: "string", description: "Dynamically generated 2-3 sentence hero narrative." },
                    whyRelevant: { type: "string", description: "1-2 sentences explaining why this skill is core to the industry." },
                    industryDemand: { type: "string", description: "Concrete hiring demand or growth statistics." },
                    whereUsed: { 
                        type: "array", 
                        description: "Top 3 to 5 real-world applications, industries, or use cases where this skill is heavily applied.",
                        items: { type: "string" }
                    }
                }
            }
        }
    },
    overview: {
        // Shared with quickStats in the backend preset
        type: "object",
        required: ["quickStats", "overview", "curationText"],
        properties: {
            curationText: { type: "string", description: "Dynamically generated 1-sentence editor's note explaining curation." },
            quickStats: {
                type: "object",
                required: ["paths", "salaryRange", "level"],
                properties: {
                    paths: { type: "integer", description: "Number of major career paths that use this skill." },
                    salaryRange: { type: "string", description: "Representative annual US salary range." },
                    level: { type: "string", description: "Beginner, Intermediate, Advanced, or Beginner to Advanced." }
                }
            },
            overview: {
                type: "object",
                required: ["whyRelevant", "industryDemand", "whereUsed", "description"],
                properties: {
                    description: { type: "string", description: "Dynamically generated 2-3 sentence hero narrative." },
                    whyRelevant: { type: "string", description: "1-2 sentences explaining why this skill is core to the industry." },
                    industryDemand: { type: "string", description: "Concrete hiring demand or growth statistics." },
                    whereUsed: { 
                        type: "array", 
                        description: "Top 3 to 5 real-world applications, industries, or use cases where this skill is heavily applied.",
                        items: { type: "string" }
                    }
                }
            }
        }
    },
    careers: {
        type: "object",
        required: ["careers"],
        properties: {
            careers: {
                type: "array",
                description: "Top 3-4 career roles for this skill.",
                items: {
                    type: "object",
                    properties: {
                        role: { type: "string", description: "Job title." },
                        trend: { type: "string", description: "Hiring trend: Rising, Stable, or Declining." },
                        salary: { type: "string", description: "Average annual salary range." },
                        description: { type: "string", description: "One sentence role summary." }
                    }
                }
            }
        }
    },
    whereUsed: {
        type: "object",
        required: ["whereUsed"],
        properties: {
            whereUsed: {
                type: "array",
                description: "Top 4-6 real-world industry domains with enterprise hiring companies and usage descriptions.",
                items: {
                    type: "object",
                    required: ["industry", "companies", "usage"],
                    properties: {
                        industry: { type: "string", description: "Industry / domain name" },
                        companies: { type: "array", items: { type: "string" }, description: "List of AT LEAST 5 major, standard, authoritative hiring companies" },
                        usage: { type: "string", description: "How this skill is applied in this industry" }
                    }
                }
            }
        }
    }
};

const COMPREHENSIVE_PROMPT = `You are an expert labor market analyst creating metadata for a public skill page on a learning marketplace.

Research the target skill using recent web sources.

Guidelines:
- Prefer official documentation, government labor statistics, and reputable industry publications.
- Use representative US salary data.
- Synthesize information across multiple sources instead of copying any single source.
- Avoid unsupported claims and marketing language.
- Keep the writing concise, factual, and suitable for learners evaluating whether to acquire this skill.
- Avoid duplicate information across fields.
- If evidence is limited or conflicting, make conservative estimates.`;

const WHERE_USED_PROMPT = `Identify 4-6 distinct real-world industry domains where this skill is heavily used in production. For EACH industry domain, list AT LEAST 5 prominent, standard, authoritative enterprise/Fortune 500 companies operating in that domain that use this skill, along with a concise 1-2 sentence explanation of how it is utilized. Do NOT include citation markers like [1] or [2].`;

const DEFAULT_PROMPTS = {
    quickStats: COMPREHENSIVE_PROMPT,
    overview: COMPREHENSIVE_PROMPT,
    careers: COMPREHENSIVE_PROMPT,
    whereUsed: WHERE_USED_PROMPT
};

const ExaSearchConfig = ({ visible, onClose, section, skillName, onApplyResults }) => {
    const { accessToken } = useSelector((state) => state.auth || {});
    const token = accessToken || localStorage.getItem("accessToken") || localStorage.getItem("token") || sessionStorage.getItem("token");

    const [searchType, setSearchType] = useState("auto");
    const [systemPrompt, setSystemPrompt] = useState("");
    const [rewritePrompt, setRewritePrompt] = useState("");
    const [saving, setSaving] = useState(false);
    
    const [testing, setTesting] = useState(false);
    const [testResults, setTestResults] = useState(null);

    // Initialization
    useEffect(() => {
        if (visible && section) {
            setSearchType("auto");
            setTestResults(null);
            
            const defaultPrompt = DEFAULT_PROMPTS[section] || "";
            setSystemPrompt(defaultPrompt);
            setRewritePrompt("Rewrite the following raw data into engaging, concise paragraphs for a skill landing page. Preserve all statistics and facts exactly. Remove citation markers. Keep it under 3 sentences per field.");

            if (token) {
                const exaCategory = `Exa - ${section}`;
                axios.get(`${appConfig.apiURL}/v2/admin/ai/config`, {
                    params: { category: exaCategory },
                    headers: { 'Authorization': `Bearer ${token}` }
                }).then(response => {
                    const data = response.data;
                    if (data?.status && data?.data) {
                        if (data.data.systemPrompt) setSystemPrompt(data.data.systemPrompt);
                        if (data.data.rewritePrompt !== undefined) setRewritePrompt(data.data.rewritePrompt);
                    }
                }).catch(err => {
                    console.error("Failed to fetch Exa config:", err);
                });
            }
        }
    }, [visible, section, token]);

    const handleSaveConfig = async () => {
        if (!token) {
            message.warning("You must be logged in to save configurations.");
            return;
        }
        setSaving(true);
        try {
            const exaCategory = `Exa - ${section}`;
            const payload = {
                category: exaCategory,
                systemPrompt: systemPrompt,
                rewritePrompt: rewritePrompt
            };
            const response = await axios.put(`${appConfig.apiURL}/v2/admin/ai/config`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data?.status) {
                message.success("Exa configuration saved successfully.");
            } else {
                message.error("Failed to save Exa configuration.");
            }
        } catch (error) {
            console.error("Save config error:", error);
            message.error("An error occurred while saving.");
        } finally {
            setSaving(false);
        }
    };

    const handleRunTest = async () => {
        if (!skillName) {
            message.warning("A skill name is required to run a test search.");
            return;
        }

        console.warn("[ExaTest] Running test search for skill:", skillName, "section:", section);
        setTesting(true);
        setTestResults(null);
        try {
            const response = await axios.post(`${appConfig.apiURL}/v2/admin/exa/skill-structured`, {
                skill: skillName,
                section,
                systemPrompt,
                rewritePrompt,
                type: searchType,
                useCache: false
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            console.warn("[ExaTest] Backend response:", response.data);

            if (response.data.status) {
                const resultsPayload = response.data.results || response.data.data || response.data;
                setTestResults(resultsPayload);
                message.success("Test search completed successfully.");
            } else {
                console.warn("[ExaTest] Backend returned status: false", response.data);
                message.error(response.data.message || "Test search failed.");
            }
        } catch (error) {
            console.error("[ExaTest] Request failed:", error);
            message.error(error.response?.data?.message || error.message || "Failed to run test search.");
        } finally {
            setTesting(false);
        }
    };

    const handleApply = () => {
        if (onApplyResults && testResults) {
            onApplyResults(testResults);
            message.success("Data applied to form.");
            onClose();
        }
    };

    const extractStructuredData = (data) => {
        if (!data) return {};
        
        const findKeys = (obj) => {
            if (!obj) return null;
            if (typeof obj === 'string') {
                try {
                    const parsed = JSON.parse(obj);
                    if (parsed && typeof parsed === 'object') {
                        return findKeys(parsed);
                    }
                } catch (e) {}
                return null;
            }
            if (typeof obj !== 'object') return null;
            
            if (obj.quickStats || obj.overview || obj.careers) {
                return obj;
            }
            
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const val = obj[key];
                    if (typeof val === 'string') {
                        try {
                            const parsed = JSON.parse(val);
                            if (parsed && typeof parsed === 'object') {
                                const res = findKeys(parsed);
                                if (res) return res;
                            }
                        } catch (e) {}
                    } else if (typeof val === 'object' && val !== null) {
                        const res = findKeys(val);
                        if (res) return res;
                    }
                }
            }
            return null;
        };
        
        const common = data.output?.structured || data.output || data.structured || data.data?.output || data.data;
        const commonRes = findKeys(common);
        if (commonRes) return commonRes;
        
        const deepRes = findKeys(data);
        if (deepRes) return deepRes;
        
        return data;
    };

    const safeRenderVal = (val) => {
        if (val === null || val === undefined) return "";
        if (typeof val === 'object') {
            return JSON.stringify(val);
        }
        return String(val);
    };

    const renderPreview = () => {
        console.warn("[ExaTest] renderPreview triggered. testResults:", testResults);
        if (!testResults) return null;

        const structuredData = extractStructuredData(testResults);
        console.warn("[ExaTest] extracted structuredData:", structuredData);

        if (section === "quickStats" || section === "overview") {
            const qs = structuredData.quickStats;
            const ov = structuredData.overview;
            
            if (!qs && !ov) {
                return (
                    <div style={{ background: "#fff", padding: 8, borderRadius: 4, maxHeight: 250, overflowY: "auto", border: "1px solid #e8e8e8" }}>
                        <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(testResults, null, 2)}</pre>
                    </div>
                );
            }
            
            return (
                <div style={{ marginTop: 12, maxHeight: 350, overflowY: 'auto' }}>
                    {qs && (
                        <Descriptions title="Quick Stats" bordered size="small" column={1} style={{ marginBottom: 16, background: '#fff' }}>
                            <Descriptions.Item label="Paths">{safeRenderVal(qs.paths)}</Descriptions.Item>
                            <Descriptions.Item label="Salary Range">{safeRenderVal(qs.salaryRange)}</Descriptions.Item>
                            <Descriptions.Item label="Level">{safeRenderVal(qs.level)}</Descriptions.Item>
                            {qs.demandSignal && <Descriptions.Item label="Demand Signal">{safeRenderVal(qs.demandSignal)}</Descriptions.Item>}
                        </Descriptions>
                    )}
                    {ov && (
                        <Descriptions title="Overview" bordered size="small" column={1} style={{ background: '#fff' }}>
                            <Descriptions.Item label="Why Relevant">{safeRenderVal(ov.whyRelevant)}</Descriptions.Item>
                            <Descriptions.Item label="Industry Demand">{safeRenderVal(ov.industryDemand)}</Descriptions.Item>
                        </Descriptions>
                    )}
                </div>
            );
        } else if (section === "careers" || section === "careerOutcomes") {
            const careers = Array.isArray(structuredData.careers) ? structuredData.careers : [];
            
            if (careers.length === 0) {
                return (
                    <div style={{ background: "#fff", padding: 8, borderRadius: 4, maxHeight: 250, overflowY: "auto", border: "1px solid #e8e8e8" }}>
                        <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(testResults, null, 2)}</pre>
                    </div>
                );
            }
            
            return (
                <div style={{ marginTop: 12, maxHeight: 350, overflowY: 'auto' }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 16 }}>Careers</div>
                    <List
                        dataSource={careers}
                        renderItem={item => (
                            <List.Item style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, marginBottom: 8, padding: 12 }}>
                                <div style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Typography.Text strong>{safeRenderVal(item.role)}</Typography.Text>
                                        <Tag color={item.trend?.toLowerCase() === 'rising' ? 'green' : item.trend?.toLowerCase() === 'declining' ? 'red' : 'blue'}>{safeRenderVal(item.trend)}</Tag>
                                    </div>
                                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Salary: {safeRenderVal(item.salary)}</div>
                                    <div style={{ fontSize: 13 }}>{safeRenderVal(item.description)}</div>
                                </div>
                            </List.Item>
                        )}
                    />
                </div>
            );
        }

        return (
            <div style={{ 
                background: "#fff", 
                padding: 8, 
                borderRadius: 4, 
                maxHeight: 250, 
                overflowY: "auto",
                fontFamily: "monospace",
                fontSize: 12,
                border: "1px solid #e8e8e8"
            }}>
                <pre style={{ margin: 0 }}>
                    {JSON.stringify(testResults, null, 2)}
                </pre>
            </div>
        );
    };

    return (
        <Modal
            title={<div style={{ display: "flex", alignItems: "center", gap: 8 }}><GlobalOutlined style={{ color: "#1890ff" }} /> Exa Search Configuration — {section}</div>}
            open={visible}
            onCancel={onClose}
            width={850}
            footer={null}
            destroyOnClose
            centered
            bodyStyle={{ padding: 0 }}
        >
            <Tabs defaultActiveKey="parameters" tabPosition="left" style={{ minHeight: 450 }}>
                {/* TAB 1: PARAMETERS */}
                <TabPane tab={<span><SettingOutlined /> Parameters</span>} key="parameters" style={{ padding: "16px 24px" }}>
                    <div style={{ marginBottom: 24 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Search Type</div>
                        <Select
                            style={{ width: '100%' }}
                            value={searchType}
                            onChange={setSearchType}
                            options={[
                                { label: 'Auto (Recommended)', value: 'auto' },
                                { label: 'Instant', value: 'instant' },
                                { label: 'Fast', value: 'fast' },
                                { label: 'Deep (Requires more tokens)', value: 'deep' }
                            ]}
                        />
                        <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                            Deep search navigates through multiple links but takes longer.
                        </div>
                    </div>
                </TabPane>

                {/* TAB 2: PROMPTS & SCHEMA */}
                <TabPane tab={<span><ThunderboltOutlined /> Prompts & Schema</span>} key="prompts" style={{ padding: "16px 24px" }}>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>System Prompt (Search Instructions)</span>
                            <Button type="primary" size="small" onClick={handleSaveConfig} loading={saving}>
                                Save Configuration
                            </Button>
                        </div>
                        <TextArea 
                            rows={4} 
                            value={systemPrompt} 
                            onChange={e => setSystemPrompt(e.target.value)} 
                            placeholder="Instructions for the search synthesizer..."
                        />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 600, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>Creative Rewrite Prompt (AI Polish Layer)</span>
                        </div>
                        <TextArea 
                            rows={4} 
                            value={rewritePrompt} 
                            onChange={e => setRewritePrompt(e.target.value)} 
                            placeholder="Instructions for creatively rewriting the search results..."
                        />
                        <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                            Leave blank to skip the creative rewrite step.
                        </div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>Output Schema (Read-only)</div>
                        <Alert 
                            type="info" 
                            message="The schema is locked for this section to ensure it matches the form fields exactly." 
                            showIcon 
                            style={{ marginBottom: 12 }} 
                        />
                        <div style={{ 
                            background: "#282c34", 
                            color: "#abb2bf", 
                            padding: 12, 
                            borderRadius: 6, 
                            maxHeight: 250, 
                            overflowY: "auto",
                            fontFamily: "monospace",
                            fontSize: 12 
                        }}>
                            <pre style={{ margin: 0 }}>
                                {JSON.stringify(SCHEMAS[section] || {}, null, 2)}
                            </pre>
                        </div>
                    </div>
                </TabPane>

                {/* TAB 3: TEST & PREVIEW */}
                <TabPane tab={<span><ExperimentOutlined /> Test & Preview</span>} key="test" style={{ padding: "16px 24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div>
                            <div style={{ fontWeight: 600 }}>Test Search</div>
                            <div style={{ fontSize: 12, color: "#666" }}>Run a real-time web search for "{skillName}"</div>
                        </div>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleRunTest} loading={testing}>
                            Run Test Search
                        </Button>
                    </div>

                    {testing ? (
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 12, color: "#888" }}>Searching the web and synthesizing results...</div>
                        </div>
                    ) : testResults ? (
                        <div>
                            <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", padding: 12, borderRadius: 6, marginBottom: 16 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                    <span style={{ fontWeight: 600, color: "#389e0d" }}>Search Successful</span>
                                    <Button type="primary" size="small" onClick={handleApply}>Apply to Form</Button>
                                </div>
                                {renderPreview()}
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#ccc", border: "1px dashed #e8e8e8", borderRadius: 8 }}>
                            Click "Run Test Search" to see real-time data.
                        </div>
                    )}
                </TabPane>
            </Tabs>
        </Modal>
    );
};

export default ExaSearchConfig;
